import { sanitize } from '@core/shared';
import { BaseApiResponse } from '@core/swagger-client';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { AccessDenied, Warning } from '../Icons';
import { ContainerDefault } from '../Layouts';

export const ErrorMessagePage = ({
    errorSubject,
    apiError,
    errorCode,
    statusCode,
    errorHeader,
    children,
    values
}: {
    errorSubject?: string;
    apiError?: BaseApiResponse;
    errorCode?: string;
    statusCode?: number;
    errorHeader?: string;
    children?: ReactNode;
    values?: {};
}) => {
    const { t } = useTranslation();

    errorSubject ??= 'Generic.ApiErrorCode.';
    if (errorSubject.charAt(errorSubject.length - 1) !== '.') {
        errorSubject += '.';
    }

    let isAccessDenied = apiError?.statusCode === 401;
    let errorBody = '';
    if (!errorHeader) {
        errorCode ??= apiError?.errorCode;
        if (!errorCode) {
            statusCode ??= apiError?.statusCode;
            if (!statusCode) {
                statusCode = 500;
            }

            switch (statusCode) {
                case 409:
                    errorCode = 'ConflictException';
                    break;
                case 401:
                    errorCode = 'AccessDeniedException';
                    break;
                case 404:
                    errorCode = 'NotFoundException';
                    break;
                case 503:
                    errorCode = 'ServiceUnavailableException';
                    break;
                case 500:
                    errorCode = 'Exception';
                    break;
            }
        }
        errorCode ??= 'Exception';

        let errorHeaderKey = errorSubject + errorCode;
        errorHeader = t(errorHeaderKey, values);
        if (errorHeader === errorHeaderKey) {
            errorSubject = 'Generic.ApiErrorCode.';
            errorHeaderKey = errorSubject + errorCode;
            errorHeader = t(errorHeaderKey, values);
            if (errorHeader === errorHeaderKey) {
                errorCode = 'Exception';
                errorHeader = t(errorSubject + errorCode, values);
            }
        }

        let errorBodyKey = errorSubject + errorCode + '.Body';
        errorBody = t(errorBodyKey, values);
        if (errorBody === errorBodyKey) {
            errorBody = '';
        }
    }

    errorHeader = sanitize(errorHeader ?? '');
    errorBody = sanitize(errorBody ?? '');

    return (
        <ContainerDefault>
            <div className="max-w-4xl">
                {isAccessDenied && <AccessDenied classNames="w-10 md:w-12 lg:w-16 stroke-warning mb-4" />}
                {!isAccessDenied && <Warning classNames="w-10 md:w-12 lg:w-16 stroke-warning mb-4" />}
                <h1
                    className="text-3xl md:text-4xl font-w-heading font-accent wrap-break-word text-balance max-w-full mb-2 md:mb-6"
                    dangerouslySetInnerHTML={{ __html: errorHeader! }}
                ></h1>
                {errorBody && <p className="pb-8" dangerouslySetInnerHTML={{ __html: errorBody! }}></p>}
                {children}
            </div>
        </ContainerDefault>
    );
};
